import { FC, useEffect, useMemo, useState } from 'react';
import { CheckBox } from '../CheckBox.tsx';
import styles from './MultipleSelector.module.scss';

export type MultipleSelectorOptionId = string | number;

export type MultipleSelectorProps = {
  options: MultipleSelectorOption[];
  showSelectAll?: boolean;
  selectAllOnMount?: boolean;
  search?: string;
  onSelectionChange?: (selectedOptions: MultipleSelectorOption[]) => void;
};

export type MultipleSelectorOption = {
  id: MultipleSelectorOptionId;
  label: string;
  disabled?: boolean;
};

export const MultipleSelector: FC<MultipleSelectorProps> = (props) => {
  const [selectedOptionIds, _setSelectedOptionIds] = useState<
    MultipleSelectorOptionId[]
  >([]);
  const setSelectedOptionIds = (
    selectedOptionIds: MultipleSelectorOptionId[],
  ) => {
    _setSelectedOptionIds(selectedOptionIds);
    props.onSelectionChange?.(
      props.options.filter((option) => selectedOptionIds.includes(option.id)),
    );
  };

  const optionsToDisplay = useMemo(() => {
    return props.options.filter((option) => {
      return option.label
        .toLowerCase()
        .includes(props.search?.toLowerCase() || '');
    });
  }, [props.options, props.search]);

  // Cleanup selected options that are not in the list of options
  useEffect(() => {
    _setSelectedOptionIds(
      selectedOptionIds.filter((id) =>
        props.options.some((option) => option.id === id),
      ),
    );
  }, [props.options]);

  useEffect(() => {
    if (props.selectAllOnMount) {
      setSelectedOptionIds(props.options.map((option) => option.id));
    }
  }, [props.selectAllOnMount, props.options]);

  const selectOption = (optionId: MultipleSelectorOptionId) => {
    setSelectedOptionIds([...selectedOptionIds, optionId]);
  };
  const deselectOption = (optionId: MultipleSelectorOptionId) => {
    setSelectedOptionIds(selectedOptionIds.filter((id) => id !== optionId));
  };
  const selectAll = () => {
    setSelectedOptionIds(props.options.map((option) => option.id));
  };
  const deselectAll = () => {
    setSelectedOptionIds([]);
  };

  return (
    <div className={styles.list}>
      {props.showSelectAll && (
        <MultipleSelectorItem
          key={'select-all'}
          option={{ id: 'select-all', label: 'Select all' }}
          isSelected={
            selectedOptionIds.length !== 0 &&
            selectedOptionIds.length === optionsToDisplay.length
          }
          onSelect={selectAll}
          onDeselect={deselectAll}
        />
      )}
      {optionsToDisplay.map((option) => (
        <MultipleSelectorItem
          key={option.id}
          option={option}
          isSelected={selectedOptionIds.includes(option.id)}
          onSelect={() => selectOption(option.id)}
          onDeselect={() => deselectOption(option.id)}
        />
      ))}
    </div>
  );
};

type MultipleSelectorItemProps = {
  option: MultipleSelectorOption;
  isSelected: boolean;
  onSelect: () => void;
  onDeselect: () => void;
};

const MultipleSelectorItem: FC<MultipleSelectorItemProps> = (props) => {
  return (
    <div className={styles.itemWrapper}>
      <div
        onClick={() => {
          props.isSelected ? props.onDeselect() : props.onSelect();
        }}
        className={styles.checkboxWrapper}
      >
        <CheckBox checked={props.isSelected} className={styles.checkbox} />
      </div>

      <p className={styles.label}>{props.option.label}</p>
    </div>
  );
};
